import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@resideo/blueprint-react";
import { Checkbox } from "@resideo/blueprint-oneportal";
import ColumnsIcon from "@/assets/icons/columns.svg?react";
import DragHandleIcon from "@/assets/icons/drag-dots.svg?react";
import styles from "./ManageDevicesColumnsMenu.module.css";
import type { DeviceColumnKey } from "../ManageDevicesTable/ManageDevicesTable";

export interface ColumnOption {
  key: DeviceColumnKey;
  label: string;
  visible: boolean;
}

export interface ManageDevicesColumnsMenuProps {
  columns: ColumnOption[];
  onToggle: (key: DeviceColumnKey) => void;
  /** Called with the new left-to-right key order after a drag-and-drop. */
  onReorder?: (orderedKeys: DeviceColumnKey[]) => void;
}

// Gap between the trigger button and the flyout, in pixels.
const MENU_OFFSET = -5;
// Extra right-edge offset so the flyout doesn't sit flush against the
// table's scrollbar when the table content overflows.
const MENU_RIGHT_OFFSET = 1;

export const ManageDevicesColumnsMenu = ({
  columns,
  onToggle,
  onReorder,
}: ManageDevicesColumnsMenuProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Anchor the (portaled) menu directly under the trigger button so it never
  // looks detached, regardless of any clipping/stacking ancestors.
  const updatePosition = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition({
      top: rect.bottom + MENU_OFFSET,
      right: window.innerWidth - rect.right + MENU_RIGHT_OFFSET,
    });
  };

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open]);

  // Close the popover when clicking outside or pressing Escape; keep it
  // anchored to the button on scroll/resize since it renders in a portal.
  useEffect(() => {
    if (!open) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !buttonRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  const resetDrag = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDrop = (targetIndex: number) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      resetDrag();
      return;
    }
    const keys = columns.map((column) => column.key);
    const [moved] = keys.splice(dragIndex, 1);
    keys.splice(targetIndex, 0, moved);
    onReorder?.(keys);
    resetDrag();
  };

  return (
    <div className={styles.container}>
      <Button
        ref={buttonRef}
        variant="secondary"
        className={styles.iconButton}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("manageDevices.ariaLabels.columns")}
      >
        <ColumnsIcon className={styles.icon} aria-hidden="true" />
      </Button>

      {open &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.menu}
            style={{ top: position.top, right: position.right }}
            role="menu"
            aria-label={t("manageDevices.ariaLabels.toggleColumns")}
          >
            <p className={styles.menuTitle}>{t("manageDevices.columnsTitle")}</p>
            {columns.map((column, index) => (
              <label
                key={column.key}
                className={`${styles.menuItem} ${
                  dragIndex === index ? styles.dragging : ""
                } ${
                  overIndex === index && dragIndex !== index
                    ? styles.dragOver
                    : ""
                }`}
                draggable
                onDragStart={() => setDragIndex(index)}
                onDragOver={(event) => {
                  event.preventDefault();
                  setOverIndex(index);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  handleDrop(index);
                }}
                onDragEnd={resetDrag}
              >
                <DragHandleIcon className={styles.dragHandle} aria-hidden="true" />
                <Checkbox
                  className={styles.checkbox}
                  checked={column.visible}
                  onChange={() => onToggle(column.key)}
                />
                <span className={styles.itemLabel}>{t(column.label)}</span>
              </label>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ManageDevicesColumnsMenu;
